'use client';
import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import LoginModal from '@/app/components/LoginModal';
import { Calendar, Clock, Bell, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

interface PublishedEvent {
  id: string;
  title: string;
  date: string;
  category: string;
  coverImage: string;
}

export default function EventsClient() {
  const { t } = useLanguage();
  const [loginOpen, setLoginOpen] = useState(false);
  const [publishedEvents, setPublishedEvents] = useState<PublishedEvent[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('events')
      .select('id, title, event_date_text, category, cover_image')
      .eq('event_status', 'published')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) {
          setPublishedEvents(data.map((e: any) => ({
            id: e.id,
            title: e.title,
            date: e.event_date_text || '',
            category: e.category || '',
            coverImage: e.cover_image || '',
          })));
        }
        setLoaded(true);
      });
  }, []);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F7F7F2' }}>
      <Header onLoginClick={() => setLoginOpen(true)} />

      <main style={{ paddingTop: 0 }}>
        {/* Page Header */}
        <div style={{ backgroundColor: '#070707', borderBottom: '1px solid #111111' }}>
          <div style={{ maxWidth: 1536, margin: '0 auto', padding: '48px 24px 40px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 16, backgroundColor: '#B8FF00', padding: '4px 10px', borderRadius: 2 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#070707', textTransform: 'uppercase', fontFamily: 'var(--font-mono, monospace)' }}>
                {t('Upcoming Events', '即将举办的活动')}
              </span>
            </div>
            <h1 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: '#FFFFFF', letterSpacing: '-0.02em', textTransform: 'uppercase', lineHeight: 0.95 }}>
              {t('Events', '活动')}
            </h1>
          </div>
        </div>

        <div style={{ maxWidth: 1536, margin: '0 auto', padding: '80px 24px' }}>
          {/* Show published events if any */}
          {loaded && publishedEvents.length > 0 ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
                {publishedEvents.map((ev) => (
                  <Link key={ev.id} href={`/events/${ev.id}`} style={{ display: 'block', textDecoration: 'none' }}>
                    <div style={{ backgroundColor: '#FFFFFF', border: '1px solid #D8D8CE', borderRadius: 4, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                      <div style={{ aspectRatio: '16/9', backgroundColor: '#F0F0EA', overflow: 'hidden' }}>
                        {ev.coverImage ? (
                          <img src={ev.coverImage} alt={ev.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Calendar size={32} color="#5F625F" />
                          </div>
                        )}
                      </div>
                      <div style={{ padding: '16px 20px' }}>
                        {ev.category && (
                          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#070707', fontFamily: 'var(--font-mono, monospace)', backgroundColor: '#B8FF00', padding: '2px 8px', borderRadius: 2, display: 'inline-block', marginBottom: 8 }}>
                            {ev.category}
                          </span>
                        )}
                        <h3 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 18, color: '#070707', marginBottom: 6, lineHeight: 1.2 }}>{ev.title}</h3>
                        {ev.date && <p style={{ fontSize: 13, color: '#5F625F', fontFamily: 'var(--font-mono, monospace)' }}>{ev.date}</p>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            /* Coming Soon State */
            <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
              <div style={{ width: 72, height: 72, borderRadius: 2, border: '1px solid #D8D8CE', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 32px', backgroundColor: '#FFFFFF' }}>
                <Calendar size={32} color="#5F625F" />
              </div>

              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, backgroundColor: '#EDFFD0', border: '1px solid #B8FF00', borderRadius: 2, padding: '6px 14px', marginBottom: 24 }}>
                <Clock size={12} color="#070707" />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#070707', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'var(--font-mono, monospace)' }}>
                  {t('Coming Soon', '即将开放')}
                </span>
              </div>

              <h2 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 24, color: '#070707', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '-0.01em', lineHeight: 1.1 }}>
                {t('Events are being prepared.', '活动正在筹备中。')}
              </h2>
              <p style={{ fontSize: 15, color: '#5F625F', marginBottom: 8, lineHeight: 1.7, fontFamily: 'var(--font-body, sans-serif)' }}>
                {t('Confirmed runs, races, and community sessions will be announced here soon.', '已确认的跑步、比赛和社群活动将很快在此公布。')}
              </p>

              <div style={{ borderTop: '1px solid #D8D8CE', marginBottom: 40, marginTop: 32 }} />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }} className="sm:flex-row sm:justify-center">
                <Link href="/weekly-plan" className="btn-primary" style={{ display: 'inline-flex' }}>
                  {t('View Weekly Training', '查看每周训练')}
                  <ArrowRight size={13} style={{ marginLeft: 6 }} />
                </Link>
                <Link href="/sign-up-login" className="btn-secondary" style={{ display: 'inline-flex' }}>
                  {t('Join the Community', '加入社群')}
                </Link>
              </div>

              <p style={{ marginTop: 28, fontSize: 11, color: '#5F625F', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.04em' }}>
                <Bell size={11} />
                {t('Members will be notified when events open for registration.', '活动开放报名时，会员将收到通知。')}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} onSuccess={() => setLoginOpen(false)} />
    </div>
  );
}